import { useEffect, useMemo, useState } from 'react'
import { versionLabel } from '@/lib/versioning'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Loader2,
  AlertCircle,
  Plus,
  Save,
  Search,
  Check,
  Flag,
  GitMerge,
  MousePointerClick,
  Clock,
  X,
  ChevronUp,
  ChevronDown,
  Lock,
  Repeat2,
} from 'lucide-react'
import { Breadcrumbs } from '@/components/layout/Breadcrumbs'
import { BackLink, useBackNav, useBackNavLinkBuilder } from '@/components/navigation/BackLink'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { DAY_MS, formatLagExact } from '@/lib/lag'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { procedureTemplatesApi } from '@/api/procedureTemplatesApi'
import { formsApi } from '@/api/formsApi'
import { ProcedureFlowBuilder, type EdgeLagMap } from '@/components/procedures/ProcedureFlowBuilder'
import { useFlowCanvasHeight } from '@/hooks/useFlowCanvasHeight'
import { wouldCreateCycle } from '@/lib/procedureFlowGraph'
import { formOffersRepeat, repeatSeedFor } from '@/lib/repeats'
import type { ProcedureTemplateDetail, ProcedureTemplateStepEdge, FormDefinition } from '@/types/cgt'
import { PageHeading } from '@/components/layout/PageHeading'

interface EditStep {
  key: string
  formDefinitionId: string
  formName: string
  /**
   * How many times this step is performed; null = does not repeat.
   *
   * Seeded by the server from the form's defaultRepeatCount when a step is
   * first added, then owned by this template — editing the form's default never
   * rewrites templates already built on it.
   */
  repeatCount: number | null
  predecessorKeys: Set<string>
}

/** Builds the `${predKey}->${stepKey}` lag map from a procedure template's edges. */
function edgeLagFromEdges(edges: ProcedureTemplateStepEdge[]): EdgeLagMap {
  const map: EdgeLagMap = {}
  for (const e of edges) {
    if (e.minLagDays != null || e.maxLagDays != null) {
      map[`${e.predecessorStepId}->${e.successorStepId}`] = {
        minLagDays: e.minLagDays ?? undefined,
        maxLagDays: e.maxLagDays ?? undefined,
      }
    }
  }
  return map
}

/** Splits a fractional-days lag value into whole Days + whole Hours (0–23) for
 * display, e.g. 1.5 → { days: 1, hours: 12 }, 2 → { days: 2, hours: 0 }. */
function splitLagDays(value: number | undefined): { days: string; hours: string } {
  if (value == null) return { days: '', hours: '' }
  const totalHours = Math.round(value * 24)
  return { days: String(Math.floor(totalHours / 24)), hours: String(totalHours % 24) }
}

/** Combines Days + Hours input strings into a fractional-days value
 * (`days + hours/24`); undefined ("no constraint") only when both are empty.
 * An Hours value ≥24 (the input's `max` is only a soft UI hint, not enforced)
 * carries over into Days rather than being clamped/dropped. */
function combineLagDays(daysStr: string, hoursStr: string): number | undefined {
  if (daysStr.trim() === '' && hoursStr.trim() === '') return undefined
  const days = daysStr.trim() === '' ? 0 : Math.floor(Number(daysStr))
  const hours = hoursStr.trim() === '' ? 0 : Math.floor(Number(hoursStr))
  if (Number.isNaN(days) || Number.isNaN(hours) || days < 0 || hours < 0) return undefined
  return (days * 24 + hours) / 24
}

const LAG_PRESETS = [
  { label: '12h', hours: 12, days: 0 },
  { label: '1d', hours: 0, days: 1 },
  { label: '2d', hours: 0, days: 2 },
  { label: '3d', hours: 0, days: 3 },
  { label: '1w', hours: 0, days: 7 },
] as const

/**
 * Vertical stepper: increase above, value + unit in the middle, decrease
 * below — keeps +/- off to the sides where they'd compete with the native
 * number-input spinner (which is hidden here anyway).
 */
function DurationStepper({
  suffix,
  value,
  onStep,
  onInput,
  ariaLabel,
}: {
  suffix: 'h' | 'd'
  value: string
  onStep: (delta: number) => void
  onInput: (next: string) => void
  ariaLabel: string
}) {
  const parsed = value.trim() === '' ? 0 : Math.floor(Number(value))
  const atMin = value.trim() === '' || (!Number.isNaN(parsed) && parsed <= 0)

  return (
    <div className='flex w-14 flex-col items-stretch overflow-hidden rounded-md border bg-background'>
      <Button
        type='button'
        variant='ghost'
        size='icon'
        className='h-5 w-full shrink-0 rounded-none'
        aria-label={`Increase ${ariaLabel}`}
        onClick={() => onStep(1)}
      >
        <ChevronUp className='h-3 w-3' />
      </Button>
      <div className='flex flex-col items-center border-y bg-muted/20 py-0.5'>
        <Input
          type='number'
          min={0}
          inputMode='numeric'
          placeholder='—'
          aria-label={ariaLabel}
          value={value}
          onChange={e => {
            const raw = e.target.value
            if (raw.trim() === '') onInput('')
            else {
              const n = Math.floor(Number(raw))
              if (!Number.isNaN(n) && n >= 0) onInput(String(n))
            }
          }}
          className='h-6 w-full border-0 bg-transparent p-0 text-center text-sm font-semibold tabular-nums shadow-none [appearance:textfield] focus-visible:ring-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
        />
        <span className='text-[9px] font-medium uppercase leading-none text-muted-foreground'>
          {suffix}
        </span>
      </div>
      <Button
        type='button'
        variant='ghost'
        size='icon'
        className='h-5 w-full shrink-0 rounded-none'
        aria-label={`Decrease ${ariaLabel}`}
        onClick={() => onStep(-1)}
        disabled={atMin}
      >
        <ChevronDown className='h-3 w-3' />
      </Button>
    </div>
  )
}

/**
 * Compact hours + days row with presets. Local state is owned here so clearing
 * both fields can reach "no constraint". `onCommit` returns false when rejected
 * (min > max) and local state is not updated.
 */
function LagDaysHoursInput({
  label,
  initial,
  onCommit,
  compact,
}: {
  label: string
  initial: number | undefined
  onCommit: (value: number | undefined) => boolean
  compact?: boolean
}) {
  const start = splitLagDays(initial)
  const [days, setDays] = useState(start.days)
  const [hours, setHours] = useState(start.hours)

  const commit = (nextDays: string, nextHours: string) => {
    const combined = combineLagDays(nextDays, nextHours)
    const accepted = onCommit(combined)
    if (accepted) {
      const split = splitLagDays(combined)
      setDays(split.days)
      setHours(split.hours)
    }
  }

  const bumpHours = (delta: number) => {
    let h = hours.trim() === '' ? 0 : Math.floor(Number(hours)) || 0
    let d = days.trim() === '' ? 0 : Math.floor(Number(days)) || 0
    h += delta
    while (h >= 24) {
      h -= 24
      d += 1
    }
    while (h < 0) {
      if (d > 0) {
        d -= 1
        h += 24
      } else {
        h = 0
        break
      }
    }
    commit(d <= 0 ? '' : String(d), h <= 0 ? '' : String(h))
  }

  const bumpDays = (delta: number) => {
    const d = days.trim() === '' ? 0 : Math.floor(Number(days)) || 0
    const next = Math.max(0, d + delta)
    commit(next <= 0 ? '' : String(next), hours)
  }

  const combined = combineLagDays(days, hours)
  const summary = combined != null ? formatLagExact(combined * DAY_MS) : null
  const hasValue = combined != null

  return (
    <div className={cn('space-y-1.5', compact ? 'py-2' : 'rounded-lg border bg-muted/15 p-2.5')}>
      <div className='flex items-center justify-between gap-2'>
        <Label className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
          {label}
        </Label>
        <div className='flex items-center gap-1'>
          {summary && (
            <span className='rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-foreground'>
              {summary}
            </span>
          )}
          {hasValue && (
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='h-6 w-6 text-muted-foreground hover:text-foreground'
              aria-label={`Clear ${label}`}
              onClick={() => commit('', '')}
            >
              <X className='h-3 w-3' />
            </Button>
          )}
        </div>
      </div>

      <div className='flex items-start gap-1.5'>
        <DurationStepper
          suffix='h'
          value={hours}
          ariaLabel={`${label} hours`}
          onStep={bumpHours}
          onInput={next => commit(days, next)}
        />
        <DurationStepper
          suffix='d'
          value={days}
          ariaLabel={`${label} days`}
          onStep={bumpDays}
          onInput={next => commit(next, hours)}
        />
      </div>

      <div className='flex flex-wrap gap-1'>
        {LAG_PRESETS.map(preset => {
          const presetDays = preset.days <= 0 ? '' : String(preset.days)
          const presetHours = preset.hours <= 0 ? '' : String(preset.hours)
          const active =
            days === presetDays &&
            hours === presetHours &&
            !(presetDays === '' && presetHours === '')
          return (
            <button
              key={preset.label}
              type='button'
              onClick={() => commit(presetDays, presetHours)}
              className={cn(
                'rounded border px-1.5 py-0.5 text-[10px] font-medium transition-colors',
                active
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground',
              )}
            >
              {preset.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/** Right-side panel to configure min/max lag (as Days + Hours) for the selected arrow. */
/**
 * How many times the selected step is performed.
 *
 * Renders NOTHING unless the step's form offers the `Repeat` action — the
 * ticket's "a step whose form does not have Repeat shows no input". The panel
 * sits beside the lag panel rather than on the flow node because a node is a
 * box in a canvas with no room for a labelled control, and this is a per-step
 * setting like the lag is a per-edge one.
 */
function StepRepeatPanel({
  step,
  form,
  onChange,
}: {
  step: EditStep | null
  form: FormDefinition | undefined
  onChange: (key: string, next: number) => void
}) {
  if (!step || !form || !formOffersRepeat(form)) return null

  // A repeat-enabled form with no count yet still shows an input reading 1 —
  // the step repeats once until someone says otherwise, and an empty box would
  // read as "unset" for a setting that is always present.
  const value = step.repeatCount ?? 1

  return (
    // One row, not a panel. This sits beside the lag panel in a narrow column
    // and the explanation was three lines of prose to carry one number — it
    // moved to the tooltip, where it is there for whoever wants it and costs
    // nothing to whoever does not.
    <aside className='w-full shrink-0 self-start rounded-xl border bg-card px-3 py-2 lg:w-72'>
      <div className='flex items-center gap-2'>
        <Repeat2 className='h-3.5 w-3.5 shrink-0 text-muted-foreground' />
        <span
          className='min-w-0 flex-1 truncate text-xs font-medium'
          title={`${step.formName} — each repeat is filled and signed separately, and the next form opens only once every repeat is complete.`}
        >
          {step.formName}
        </span>
        <Input
          type='number'
          min={1}
          step={1}
          className='h-7 w-14 shrink-0 text-xs'
          aria-label='Repeats'
          value={value}
          onChange={e => {
            const next = Number.parseInt(e.target.value, 10)
            if (Number.isNaN(next)) return
            onChange(step.key, Math.max(1, next))
          }}
        />
        <span className='shrink-0 text-[11px] text-muted-foreground'>×</span>
      </div>
    </aside>
  )
}

function EdgeLagPanel({
  edge,
  steps,
  edgeLag,
  onChange,
}: {
  edge: { predKey: string; stepKey: string } | null
  steps: EditStep[]
  edgeLag: EdgeLagMap
  onChange: (
    predKey: string,
    stepKey: string,
    patch: { minLagDays?: number; maxLagDays?: number },
  ) => boolean
}) {
  const nameOf = (key: string) => steps.find(s => s.key === key)?.formName ?? 'form'

  if (!edge) {
    return (
      <aside className='hidden w-72 shrink-0 self-start rounded-xl border bg-muted/20 p-3 lg:block'>
        <div className='flex items-center gap-1.5'>
          <Clock className='h-3.5 w-3.5 text-muted-foreground' />
          <p className='text-sm font-semibold'>Lag time</p>
        </div>
        <p className='mt-2 text-[11px] leading-relaxed text-muted-foreground'>
          Click an arrow to set min/max wait between forms. Both are optional.
        </p>
      </aside>
    )
  }

  const lag = edgeLag[`${edge.predKey}->${edge.stepKey}`] ?? {}

  return (
    <aside
      key={`${edge.predKey}->${edge.stepKey}`}
      className='w-full shrink-0 self-start rounded-xl border bg-card p-3 lg:w-72'
    >
      <div className='flex items-center gap-1.5'>
        <Clock className='h-3.5 w-3.5 text-muted-foreground' />
        <p className='text-sm font-semibold'>Lag time</p>
      </div>
      <p className='mt-1 truncate text-[11px] text-muted-foreground'>
        <span className='font-medium text-foreground'>{nameOf(edge.predKey)}</span>
        <span className='mx-1'>→</span>
        <span className='font-medium text-foreground'>{nameOf(edge.stepKey)}</span>
      </p>

      <div className='mt-2 divide-y rounded-lg border bg-muted/10 px-2.5'>
        <LagDaysHoursInput
          compact
          label='Minimum'
          initial={lag.minLagDays}
          onCommit={v => onChange(edge.predKey, edge.stepKey, { minLagDays: v })}
        />
        <LagDaysHoursInput
          compact
          label='Maximum'
          initial={lag.maxLagDays}
          onCommit={v => onChange(edge.predKey, edge.stepKey, { maxLagDays: v })}
        />
      </div>

      <p className='mt-2 text-[10px] leading-snug text-muted-foreground'>
        Min = earliest the next form opens. Max = deadline to complete it. Leave blank for no
        limit.
      </p>
    </aside>
  )
}

export function SequenceEditorPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  // Where the user came from, when they came from somewhere that said so.
  const backTo = useBackNav()
  const [procedureTemplate, setProcedureTemplate] = useState<ProcedureTemplateDetail | null>(null)

  // Links OUT of this page carry this page — INCLUDING its own `from` — so a
  // chain unwinds one hop at a time: from the form editor, back reaches this
  // editor still holding the list, and back again reaches the list.
  const withOrigin = useBackNavLinkBuilder(procedureTemplate?.name ?? 'Procedure Template')
  const [definitions, setDefinitions] = useState<FormDefinition[]>([])
  const [steps, setSteps] = useState<EditStep[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [edgeLag, setEdgeLag] = useState<EdgeLagMap>({})
  const [selectedEdge, setSelectedEdge] = useState<{ predKey: string; stepKey: string } | null>(
    null,
  )

  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerSearch, setPickerSearch] = useState('')

  // Canvas fills from wherever it lands on the page down to the bottom of the
  // viewport, floored at 450px.
  const { ref: canvasRef, height: canvasHeight } = useFlowCanvasHeight<HTMLDivElement>()

  // Set only when the server says this version's sequence is frozen. Absent on
  // an older backend, which reads as editable — the server is still the control.
  const frozenReason = procedureTemplate?.sequenceFrozenReason ?? null

  useEffect(() => {
    if (!id) return
    setLoading(true)
    // The template's type decides the forms fetch, so it is read first rather
    // than in parallel: an Inventory-type procedure may only sequence
    // inventory-category forms, filtered server-side the same way
    // AddFormDialog/CreateCapaDialog filter theirs. Every other template type
    // keeps today's unfiltered fetch.
    procedureTemplatesApi
      .get(id)
      .then((proc) =>
        formsApi
          .listDefinitions({ active: true, category: proc.type === 'Inventory' ? 'inventory' : undefined })
          .then(({ items: defs }) => [proc, defs] as const),
      )
      .then(([proc, defs]) => {
        setProcedureTemplate(proc)
        setDefinitions(defs)
        setSteps(
          proc.steps.map(s => ({
            key: s.stepId,
            formDefinitionId: s.formDefinitionId,
            formName: s.formName,
            repeatCount: s.repeatCount ?? null,
            predecessorKeys: new Set(s.predecessors.map(p => p.stepId)),
          })),
        )
        setEdgeLag(edgeLagFromEdges(proc.edges))
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load procedure template.'))
      .finally(() => setLoading(false))
  }, [id])

  const usedFormIds = useMemo(() => new Set(steps.map(s => s.formDefinitionId)), [steps])

  const availableDefs = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase()
    return definitions
      .filter(d => d.active !== false)
      // Superseded rows (a newer ACTIVE version exists) are never offered —
      // only the latest active version of each form family (86eybvqgn).
      .filter(d => !d.latestActiveVersion)
      .filter(d => !usedFormIds.has(d.id))
      .filter(d => !q || d.name.toLowerCase().includes(q))
  }, [definitions, usedFormIds, pickerSearch])

  const markDirty = () => setSaved(false)

  const addForm = (def: FormDefinition) => {
    const key = crypto.randomUUID()
    setSteps(prev => [
      ...prev,
      {
        key,
        formDefinitionId: def.id,
        formName: def.name,
        // Seeded here as well as server-side so the input shows the form's
        // default immediately, before the sequence has been saved once.
        repeatCount: repeatSeedFor(def),
        predecessorKeys: new Set(),
      },
    ])
    setSelectedKey(key)
    setPickerOpen(false)
    setPickerSearch('')
    markDirty()
  }

  const updateRepeatCount = (key: string, next: number) => {
    setSteps(prev => prev.map(s => (s.key === key ? { ...s, repeatCount: next } : s)))
    markDirty()
  }

  const addDependency = (stepKey: string, predKey: string) => {
    setSteps(prev => {
      if (wouldCreateCycle(prev, stepKey, predKey)) return prev
      return prev.map(s =>
        s.key === stepKey
          ? { ...s, predecessorKeys: new Set(s.predecessorKeys).add(predKey) }
          : s,
      )
    })
    markDirty()
  }

  const removeDependency = (stepKey: string, predKey: string) => {
    setSteps(prev =>
      prev.map(s => {
        if (s.key !== stepKey) return s
        const pk = new Set(s.predecessorKeys)
        pk.delete(predKey)
        return { ...s, predecessorKeys: pk }
      }),
    )
    setEdgeLag(prev => {
      const next = { ...prev }
      delete next[`${predKey}->${stepKey}`]
      return next
    })
    setSelectedEdge(sel => (sel?.predKey === predKey && sel?.stepKey === stepKey ? null : sel))
    markDirty()
  }

  // Returns whether the patch was applied — false when rejected (min > max),
  // so the caller (the Days/Hours inputs) knows not to reflect an edit that
  // was never actually persisted into edgeLag.
  const updateEdgeLag = (
    predKey: string,
    stepKey: string,
    patch: { minLagDays?: number; maxLagDays?: number },
  ): boolean => {
    let applied = true
    setEdgeLag(prev => {
      const key = `${predKey}->${stepKey}`
      const merged = { ...prev[key], ...patch }
      // Reject impossible windows where the floor exceeds the ceiling. Without
      // this, downstream lag-note handling (which prioritizes availableAt over
      // dueAt) would resolve the bad config inconsistently.
      if (
        merged.minLagDays != null &&
        merged.maxLagDays != null &&
        merged.minLagDays > merged.maxLagDays
      ) {
        applied = false
        return prev
      }
      const next = { ...prev }
      if (merged.minLagDays == null && merged.maxLagDays == null) delete next[key]
      else next[key] = merged
      return next
    })
    if (applied) markDirty()
    return applied
  }

  const removeStep = (key: string) => {
    setSteps(prev =>
      prev
        .filter(s => s.key !== key)
        .map(s => {
          if (!s.predecessorKeys.has(key)) return s
          const pk = new Set(s.predecessorKeys)
          pk.delete(key)
          return { ...s, predecessorKeys: pk }
        }),
    )
    if (selectedKey === key) setSelectedKey(null)
    setEdgeLag(prev => {
      const next: EdgeLagMap = {}
      for (const [k, v] of Object.entries(prev)) {
        const [predKey, stepKey] = k.split('->')
        if (predKey !== key && stepKey !== key) next[k] = v
      }
      return next
    })
    setSelectedEdge(sel => (sel?.predKey === key || sel?.stepKey === key ? null : sel))
    markDirty()
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    const orderByKey = new Map(steps.map((s, i) => [s.key, i + 1]))
    const payload = steps.map((s, i) => ({
      formDefinitionId: s.formDefinitionId,
      sortOrder: i + 1,
      repeatCount: s.repeatCount,
      predecessors: [...s.predecessorKeys]
        .map(predKey => {
          const predecessorSortOrder = orderByKey.get(predKey)
          if (predecessorSortOrder == null) return null
          const lag = edgeLag[`${predKey}->${s.key}`]
          return {
            predecessorSortOrder,
            minLagDays: lag?.minLagDays,
            maxLagDays: lag?.maxLagDays,
          }
        })
        .filter((p): p is NonNullable<typeof p> => p != null),
    }))

    try {
      const updated = await procedureTemplatesApi.replaceSequence(id, payload)
      setProcedureTemplate(updated)
      setSteps(
        updated.steps.map(s => ({
          key: s.stepId,
          formDefinitionId: s.formDefinitionId,
          formName: s.formName,
          repeatCount: s.repeatCount ?? null,
          predecessorKeys: new Set(s.predecessors.map(p => p.stepId)),
        })),
      )
      setEdgeLag(edgeLagFromEdges(updated.edges))
      setSelectedEdge(null)
      setSaved(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save sequence.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className='flex items-center justify-center py-24'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    )
  }

  return (
    // Full-bleed rather than the usual `container`: this page is a canvas, and
    // a canvas is the one thing that gets better with every pixel it is given.
    // `container max-w-5xl` left it about a third of a desktop window wide with
    // the rest of the page empty, and plain `container` still caps at the 2xl
    // breakpoint — AC 1.1 of z8nrz7dewq asks for no max width capping it short
    // of the space available, so the padding is all that stands in for one.
    <div className='w-full px-8 py-8'>
      <BackLink className='mb-4' />

      {/* The trail is dropped entirely when a back link is showing: its origin
          crumb would repeat the link's words on the line above, and its last
          crumb only ever repeats the <h1> below. Without a `from` it stays
          exactly as it was — still the right trail for the standalone
          /procedure-templates route this page is also reached by (z8nrz7czdw). */}
      {!backTo && (
        <Breadcrumbs
          items={[
            { label: 'Procedure Templates', to: '/procedure-templates' },
            { label: procedureTemplate?.name ?? id },
          ]}
          className='mb-4'
        />
      )}

      <div className='mb-5 flex items-start justify-between gap-4'>
        <PageHeading title={procedureTemplate?.name} description='Drag forms onto the canvas and connect them to define the flow. Each arrow means the target form waits for the source to be completed.' />
        <div className='flex shrink-0 gap-2'>
          <Button variant='outline' onClick={() => setPickerOpen(true)} disabled={!!frozenReason}>
            <Plus className='mr-2 h-4 w-4' />
            Add form
          </Button>
          <Button onClick={handleSave} disabled={saving || !!frozenReason}>
            {saving ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              <Save className='mr-2 h-4 w-4' />
            )}
            Save flow
          </Button>
        </div>
      </div>

      {/* A superseded or retired version is a record, not a draft. The server
          refuses the save either way (z8nrz7ckjj); saying so here means nobody
          rebuilds a flow only to have it rejected on submit. */}
      {frozenReason && (
        <div className='mb-4 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900'>
          <Lock className='h-4 w-4 shrink-0' />
          <span>{frozenReason}</span>
        </div>
      )}

      {error && (
        <div className='mb-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
          <AlertCircle className='h-4 w-4 shrink-0' />
          {error}
        </div>
      )}
      {saved && !error && (
        <div className='mb-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700'>
          <Check className='h-4 w-4 shrink-0' />
          Flow saved.
        </div>
      )}

      {/* Legend / hints */}
      <div className='mb-3 flex flex-wrap gap-2 text-xs text-muted-foreground'>
        <span className='inline-flex items-center gap-1 rounded-full border bg-white px-2 py-0.5'>
          <Flag className='h-3 w-3 text-emerald-500' />
          Start = no incoming arrows
        </span>
        <span className='inline-flex items-center gap-1 rounded-full border bg-white px-2 py-0.5'>
          <GitMerge className='h-3 w-3 text-amber-500' />
          Merge = waits for 2+ forms
        </span>
        <span className='inline-flex items-center gap-1 rounded-full border bg-white px-2 py-0.5'>
          <MousePointerClick className='h-3 w-3' />
          Drag dot → dot to connect · click an arrow to set lag time · arrow + Backspace to disconnect
        </span>
      </div>

      {steps.length === 0 ? (
        <div className='flex h-[520px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed text-center text-sm text-muted-foreground'>
          <p>No forms in this flow yet.</p>
          <Button variant='outline' onClick={() => setPickerOpen(true)}>
            <Plus className='mr-2 h-4 w-4' />
            Add the first form
          </Button>
        </div>
      ) : (
        <div className='flex flex-col gap-4 lg:flex-row'>
          {/* The canvas column takes every pixel the Lag time panel (a fixed
              w-72, shrink-0 aside) leaves, and runs from here to the bottom of
              the viewport — see useFlowCanvasHeight for why the height is
              measured rather than expressed as a calc(). */}
          <div ref={canvasRef} className='min-w-0 flex-1' style={{ height: canvasHeight }}>
            <ProcedureFlowBuilder
              className='h-full'
              steps={steps}
              definitions={definitions}
              selectedKey={selectedKey}
              onSelectStep={setSelectedKey}
              onAddDependency={addDependency}
              onRemoveDependency={removeDependency}
              onRemoveStep={removeStep}
              onEditForm={fid => navigate(withOrigin(`/form-builder/${fid}`))}
              edgeLag={edgeLag}
              selectedEdgeId={
                selectedEdge ? `${selectedEdge.predKey}->${selectedEdge.stepKey}` : null
              }
              onSelectEdge={setSelectedEdge}
            />
          </div>
          <div className='flex w-full shrink-0 flex-col gap-4 lg:w-72'>
            <StepRepeatPanel
              step={steps.find(s => s.key === selectedKey) ?? null}
              form={definitions.find(
                d => d.id === steps.find(s => s.key === selectedKey)?.formDefinitionId,
              )}
              onChange={updateRepeatCount}
            />
            <EdgeLagPanel
              edge={selectedEdge}
              steps={steps}
              edgeLag={edgeLag}
              onChange={updateEdgeLag}
            />
          </div>
        </div>
      )}

      {/* Pick form to add */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a form</DialogTitle>
            <DialogDescription>
              The form drops onto the canvas with no dependencies. Connect it by dragging.
            </DialogDescription>
          </DialogHeader>
          <div className='relative'>
            <Search className='absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
            <Input
              autoFocus
              placeholder='Search forms by name…'
              value={pickerSearch}
              onChange={e => setPickerSearch(e.target.value)}
              className='pl-8'
            />
          </div>
          <div className='max-h-72 space-y-1 overflow-y-auto'>
            {availableDefs.length === 0 ? (
              <p className='py-6 text-center text-sm text-muted-foreground'>
                {definitions.length === usedFormIds.size
                  ? 'All forms are already in this procedure.'
                  : 'No matching forms.'}
              </p>
            ) : (
              availableDefs.map(def => (
                <button
                  key={def.id}
                  type='button'
                  onClick={() => addForm(def)}
                  className='flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition-colors hover:border-blue-300 hover:bg-blue-50/40'
                >
                  <span className='truncate font-medium'>{def.name}</span>
                  {/* Same label the canvas node shows for the step this adds
                      (z8nrz7cnhh) — a picker reading `v3` beside a node reading
                      `V3` invites the reader to wonder whether they differ. */}
                  <span className='ml-2 shrink-0 text-xs text-muted-foreground'>
                    {def.versionName ?? versionLabel(def.version)}
                  </span>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
